import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Globe, BookOpen, Heart, Award, TrendingUp, Stethoscope, GraduationCap, Mail } from 'lucide-react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import { DonationPopup } from '../components/public/DonationModal';
import PartnerCarousel from '../components/public/PartnerCarousel';
import { YouTubeCarousel } from '../components/public/YouTubeEmbed';
import AdvertisementBoard from '../components/public/AdvertisementBoard';
import VendorsDisplay from '../components/VendorsDisplay';
import presidentImage from '../assets/president.jpg';
import bgVideo from '../assets/bgvideo.mp4';
import introVideo from '../assets/videos/intro vid.mp4';
import strongHuangImg from '../assets/advisory/Strong Huang MedTech Advisor.jpg';
import portugueseVideo from '../assets/webvideo/Portuguese.mp4';
import frenchVideo from '../assets/webvideo/FRENCH.mp4';
import arabicVideo from '../assets/webvideo/ARABIC.mp4';

// Import collaborator and partner images
import globalAsthmaImg from '../assets/collaborators and partners/global Asthma and COPD Outreach.png';
import globalHealthImg from '../assets/collaborators and partners/global health alliance.webp';
import iarcImg from '../assets/collaborators and partners/IARC-Logo.jpg';
import breatheWellImg from '../assets/collaborators and partners/the breathe well initiative.png';


import lebanonImg from '../assets/chapters/Lebanon.jpeg';
import liberiaImg from '../assets/chapters/Liberia.1.jpeg';
import earcImg from '../assets/chapters/earc.png';
import egyptImg from '../assets/chapters/egypt.jpg';
import garcImg from '../assets/chapters/garc.png';
import respiratoryGateImg from '../assets/chapters/respiratory_gate_logo.jpeg';

const HomePage = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = React.useState(0);
  const [collaborators, setCollaborators] = React.useState([]);
  const [loadingCollaborators, setLoadingCollaborators] = React.useState(true);
  const [internationalFlags, setInternationalFlags] = React.useState([]);
  const [loadingFlags, setLoadingFlags] = React.useState(true);

  const heroMessages = [
    'Empowering Africa\'s Respiratory Care Professionals',
    'Advancing Respiratory Care Education Across Africa',
    'Saving Lives, One Breath at a Time'
  ];

  // ARCA videos - local video first, then YouTube videos
  const arcaVideos = [
    { type: 'local', src: introVideo, title: 'ARCA Introduction' },
    { type: 'youtube', id: 'S-H_AuGxS_g', title: 'ARCA Video 1' },
    { type: 'youtube', id: '8DRdJo3nil8', title: 'ARCA Video 2' },
    { type: 'youtube', id: 'JLYMoDvh9iw', title: 'ARCA Video 3' }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % heroMessages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Fetch collaborators from API
  React.useEffect(() => {
    const fetchCollaborators = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(`${API_BASE}/collaborators/public`);
        const data = await response.json();

        if (data.success && data.data) {
          // Map database fields to component format
          const mappedCollaborators = data.data.map(collab => ({
            name: collab.organization_name,
            logo: collab.logo_image ? (collab.logo_image.startsWith('http') ? collab.logo_image : `${BACKEND_URL}${collab.logo_image}`) : null,
            url: collab.website_url || '#',
            description: collab.description || 'Partner organization'
          }));
          setCollaborators(mappedCollaborators);
        }
      } catch (error) {
        console.error('Error fetching collaborators:', error);
        // Fallback to hardcoded collaborators if API fails
        setCollaborators(hardcodedCollaborators);
      } finally {
        setLoadingCollaborators(false);
      }
    };

    fetchCollaborators();
  }, []);

  // Fetch international country flags from API
  React.useEffect(() => {
    const fetchInternationalFlags = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE}/country-flags`);
        const data = await response.json();

        if (data.success && data.data && data.data.length > 0) {
          // Map database fields to component format
          const mappedFlags = data.data.map(flag => ({
            code: flag.country_code,
            name: flag.country_name
          }));
          setInternationalFlags(mappedFlags);
        } else {
          // Fallback to hardcoded flags if no data
          setInternationalFlags([
            { code: 'us', name: 'United States' },
            { code: 'gb', name: 'United Kingdom' },
            { code: 'in', name: 'India' },
            { code: 'sa', name: 'Saudi Arabia' },
            { code: 'ae', name: 'UAE' },
            { code: 'pk', name: 'Pakistan' },
            { code: 'ph', name: 'Philippines' },
            { code: 'ca', name: 'Canada' },
            { code: 'de', name: 'Germany' },
            { code: 'jm', name: 'Jamaica' },
            { code: 'nz', name: 'New Zealand' },
            { code: 'mx', name: 'Mexico' },
            { code: 'br', name: 'Brazil' },
            { code: 'gy', name: 'Guyana' },
            { code: 'qa', name: 'Qatar' },
            { code: 'kw', name: 'Kuwait' },
            { code: 'es', name: 'Spain' },
            { code: 'tr', name: 'Turkey' },
            { code: 'lb', name: 'Lebanon' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching international flags:', error);
        // Fallback to hardcoded flags on error
        setInternationalFlags([
          { code: 'us', name: 'United States' },
          { code: 'gb', name: 'United Kingdom' },
          { code: 'in', name: 'India' },
          { code: 'sa', name: 'Saudi Arabia' },
          { code: 'ae', name: 'UAE' },
          { code: 'pk', name: 'Pakistan' },
          { code: 'ph', name: 'Philippines' },
          { code: 'ca', name: 'Canada' },
          { code: 'de', name: 'Germany' },
          { code: 'jm', name: 'Jamaica' },
          { code: 'nz', name: 'New Zealand' },
          { code: 'mx', name: 'Mexico' },
          { code: 'br', name: 'Brazil' },
          { code: 'gy', name: 'Guyana' },
          { code: 'qa', name: 'Qatar' },
          { code: 'kw', name: 'Kuwait' },
          { code: 'es', name: 'Spain' },
          { code: 'tr', name: 'Turkey' },
          { code: 'lb', name: 'Lebanon' }
        ]);
      } finally {
        setLoadingFlags(false);
      }
    };

    fetchInternationalFlags();
  }, []);

  const handlePrevVideo = () => {
    setCurrentVideoIndex((prevIndex) =>
      prevIndex === 0 ? arcaVideos.length - 1 : prevIndex - 1
    );
  };

  const handleNextVideo = () => {
    setCurrentVideoIndex((prevIndex) =>
      (prevIndex + 1) % arcaVideos.length
    );
  };

  const handleVideoSelect = (index) => {
    setCurrentVideoIndex(index);
  };

  // Impact statistics
  const impactStats = [
    { icon: Users, label: 'Members & Volunteers', value: '5,000+', color: 'primary' },
    { icon: Globe, label: 'Countries Represented', value: '25+', color: 'success' },
    { icon: BookOpen, label: 'Training Sessions', value: '150+', color: 'info' },
    { icon: Heart, label: 'Lives Impacted', value: '100,000+', color: 'danger' },
  ];

  // Programs
  const programs = [
    {
      icon: GraduationCap,
      title: 'Education & Training',
      description: 'Professional development programs and virtual university courses.',
      backDescription: 'Access virtual university courses, certification programs, continuing education units, and hands-on clinical workshops designed for respiratory care professionals across Africa.',
      link: '/programs/education-training',
      color: 'primary'
    },
    {
      icon: Award,
      title: 'Research & Innovation',
      description: 'Advancing respiratory care through research and innovation.',
      backDescription: 'Support for clinical research grants, publication opportunities in respiratory journals, and collaborative innovation projects driving the future of respiratory care in Africa.',
      link: '/programs/research-innovation',
      color: 'info'
    },
    {
      icon: TrendingUp,
      title: 'Advocacy & Awareness',
      description: 'Raising awareness for better respiratory healthcare policies.',
      backDescription: 'Policy advocacy at national and continental levels, public health campaigns, and community outreach programs to improve respiratory healthcare access and standards.',
      link: '/programs/advocacy-awareness',
      color: 'success'
    },
    {
      icon: Users,
      title: 'Student Exchange & Conferences',
      description: 'Annual conferences and exchange programs connecting professionals.',
      backDescription: 'Annual ARCA conference, international student exchange programs, networking events, and mentorship opportunities connecting respiratory care professionals worldwide.',
      link: '/programs/exchange-conferences',
      color: 'warning'
    },
  ];

  // Hardcoded collaborators as fallback
  const hardcodedCollaborators = [
    { name: 'International Council for Respiratory Care (ICRC)', logo: iarcImg, url: 'https://irccouncil.org/', description: 'Global organization advancing respiratory care education and practice' },
    { name: 'Global Health Alliance', logo: globalHealthImg, url: 'https://www.gha21.org', description: 'International health organization focused on global health initiatives' },
    { name: 'Global Asthma and COPD Outreach', logo: globalAsthmaImg, url: 'http://www.gacoglobal.org', description: 'Organization dedicated to asthma and COPD awareness and treatment' },
    { name: 'The Breath Well Initiative', logo: breatheWellImg, url: 'http://www.tbwi.org', description: 'Initiative focused on improving respiratory health worldwide' },
    { name: 'World Health Organization (WHO)', logo: null, url: 'https://www.who.int', description: 'United Nations agency for international public health' },
    { name: 'Canadian Society for Respiratory Therapists (CSRT)', logo: null, url: 'https://www.csrt.com', description: 'Professional organization for respiratory therapists in Canada' },
    { name: 'European Society for Respiratory Care', logo: null, url: 'https://www.esrc.eu', description: 'European organization promoting respiratory care excellence' },
    { name: 'American Association for Respiratory Care', logo: null, url: 'https://www.aarc.org', description: 'Professional association for respiratory care in the United States' },
    { name: 'Africa Thoracic Society', logo: null, url: 'https://www.africathoracic.org', description: 'Pan-African organization for thoracic health professionals' }
  ];

  // Chapters (sample data)
  const chapters = [
    { name: 'Ghana Association for Respiratory Care', code: 'gh', country: 'Ghana', logo: garcImg },
    { name: 'Respiratory Gate – Egypt', code: 'eg', country: 'Egypt', logo: respiratoryGateImg },
    { name: 'Egypt Respiratory Care Association', code: 'eg', country: 'Egypt', logo: egyptImg },
    { name: 'Ethiopia Association for Respiratory Care', code: 'et', country: 'Ethiopia', logo: earcImg },
    { name: 'Liberia Respiratory Care Association', code: 'lr', country: 'Liberia', logo: liberiaImg },
    { name: 'Lebanon Respiratory Care Chapter', code: 'lb', country: 'Lebanon', logo: lebanonImg },
  ];

  // News/Stories (sample data)
  const stories = [
    {
      title: 'ARCA Annual Conference 2024 Success',
      image: null,
      excerpt: 'Over 500 respiratory care professionals gathered in Accra for our biggest conference yet...',
      date: '2024-09-15',
      link: '/news/conference-2024'
    },
    {
      title: 'New Training Program Launches in Nigeria',
      image: null,
      excerpt: 'ARCA launches comprehensive respiratory care training program in partnership with Nigerian hospitals...',
      date: '2024-08-22',
      link: '/news/nigeria-training'
    },
    {
      title: 'Member Spotlight: Dr. Amara\'s Journey',
      image: null,
      excerpt: 'How one respiratory therapist is transforming patient care in rural Ethiopia...',
      date: '2024-08-10',
      link: '/news/member-spotlight-amara'
    },
  ];

  return (
    <PublicLayout isHomePage={true}>
      <SEO
        title={seoConfig.home.title}
        description={seoConfig.home.description}
        keywords={seoConfig.home.keywords}
        url={seoConfig.home.url}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          'name': 'Africa Respiratory Care Association',
          'alternateName': 'ARCA',
          'url': 'https://www.africaresp.org',
          'logo': 'https://www.africaresp.org/logo512.png',
          'description': seoConfig.home.description,
          'foundingDate': '2015',
          'contactPoint': {
            '@type': 'ContactPoint',
            'contactType': 'Customer Service',
            'email': 'info@africaresp.org'
          },
          'areaServed': {
            '@type': 'Place',
            'name': 'Africa'
          },
          'memberOf': {
            '@type': 'Organization',
            'name': 'International Association for Respiratory Care'
          },
          'numberOfEmployees': {
            '@type': 'QuantitativeValue',
            'value': '5000+'
          }
        }}
      />
      <DonationPopup />

      {/* Hero Section */}
      <section className="position-relative text-white overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Background Video */}
        <video
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ objectFit: 'cover', zIndex: 1 }}
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={bgVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark overlay for better text readability */}
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.6)', zIndex: 2 }}></div>

        <div className="container position-relative py-4 d-flex align-items-center" style={{ minHeight: '600px', zIndex: 3 }}>
          <div className="row justify-content-center text-center w-100">
            <div className="col-12 col-lg-10">
              <div className="position-relative" style={{ minHeight: '100px' }}>
                {heroMessages.map((message, index) => (
                  <h1
                    key={index}
                    className="display-5 display-md-4 display-lg-3 fw-bold mb-3 text-white position-absolute top-0 start-0 w-100"
                    style={{
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                      transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
                      opacity: index === currentMessageIndex ? 1 : 0,
                      transform: `translateY(${index === currentMessageIndex ? 0 : (index > currentMessageIndex ? 20 : -20)}px)`
                    }}
                  >
                    {message}
                  </h1>
                ))}
              </div>
              <p className="lead mb-4 text-white mt-3" style={{
                animation: 'fadeInUp 1s ease-out 0.3s both',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              }}>
                Where every breath matters. Join us in creating sustainable solutions for better breathing, better care, and better lives.
              </p>
              <div className="d-flex flex-column flex-sm-row justify-content-center gap-3" style={{ animation: 'fadeInUp 1s ease-out 0.6s both' }}>
                <Link
                  to="/membership_registration"
                  className="btn btn-success btn-lg fw-bold shadow"
                >
                  Join as a Member
                </Link>
                <Link
                  to="/get-involved/vendor-membership"
                  className="btn btn-lg fw-bold shadow text-white"
                  style={{ backgroundColor: '#556B2F' }}
                >
                  Become a Vendor
                </Link>
                <Link
                  to="/get-involved/volunteer"
                  className="btn btn-primary btn-lg fw-bold shadow"
                >
                  Volunteer
                </Link>
                <Link
                  to="/get-involved/donate"
                  className="btn btn-danger btn-lg fw-bold shadow"
                >
                  Donate Today
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Responsive typography */
          .display-5 {
            font-size: 1.75rem;
          }
          
          .display-4 {
            font-size: 2.5rem;
          }
          
          .display-3 {
            font-size: 3rem;
          }
          
          @media (min-width: 768px) {
            .display-5 {
              font-size: 2rem;
            }
            
            .display-4 {
              font-size: 3rem;
            }
            
            .display-3 {
              font-size: 3.5rem;
            }
          }
          
          @media (min-width: 992px) {
            .display-5 {
              font-size: 2.5rem;
            }
            
            .display-4 {
              font-size: 3.5rem;
            }
            
            .display-3 {
              font-size: 4rem;
            }
          }
        `}</style>
      </section>

      {/* Advertisement Board - Rotating Ads */}
      <AdvertisementBoard />

      {/* President's Message Section */}
      <section className="py-4 py-lg-5" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="card shadow border-0">
                <div className="row g-0">
                  {/* President's Message - 50% width */}
                  <div className="col-12 col-lg-6">
                    <div className="h-100 d-flex flex-column">
                      <div className="row g-0 flex-column flex-md-row flex-lg-column flex-xl-row h-100">
                        <div className="col-12 col-md-5 col-lg-12 col-xl-5">
                          <div className="h-100 overflow-hidden">
                            <img
                              src={presidentImage}
                              alt="Dorothy H. Bendah, President of ARCA"
                              className="img-fluid h-100 w-100"
                              style={{ objectFit: 'cover', objectPosition: 'center' }}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-7 col-lg-12 col-xl-7">
                          <div className="card-body p-4 p-lg-4 h-100 d-flex flex-column">
                            <div className="mb-3">
                              <h5 className="fw-bold mb-1" style={{ color: '#6B8E23' }}>Dorothy H. Bendah</h5>
                              <p className="text-muted mb-0">President, ARCA</p>
                            </div>
                            <h3 className="h4 fw-bold text-dark mb-3">Message from the President</h3>
                            <blockquote className="border-start border-primary border-3 ps-3 fst-italic mb-3 flex-grow-1">
                              "At ARCA, we believe that every breath matters. Our mission is to unite Africa's respiratory professionals to create sustainable solutions for better breathing, better care, and better lives."
                            </blockquote>
                            <p className="text-muted mb-3">
                              As we continue to grow and strengthen respiratory care across the continent, I am constantly inspired by the dedication of our members, partners, and volunteers.
                            </p>
                            <div className="mt-auto">
                              <Link to="/about/president-message" className="btn btn-primary">
                                Read Full Message <i className="fas fa-arrow-right ms-2"></i>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* YouTube Videos Carousel - 50% width */}
                  <div className="col-12 col-lg-6 d-flex align-items-center">
                    <div className="h-100 w-100 d-flex align-items-center justify-content-center p-4" style={{ backgroundColor: '#f1f5f9' }}>
                      <div className="w-100" style={{ maxWidth: '600px' }}>
                        <h3 className="h4 fw-bold text-dark mb-3 text-center">ARCA at a Glance</h3>

                        {/* Video Display */}
                        <div className="position-relative">
                          <div className="ratio ratio-16x9 rounded shadow overflow-hidden">
                            {arcaVideos[currentVideoIndex].type === 'local' ? (
                              <video
                                key={currentVideoIndex}
                                controls
                                className="w-100 h-100"
                                style={{ objectFit: 'contain' }}
                              >
                                <source src={arcaVideos[currentVideoIndex].src} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            ) : (
                              <iframe
                                key={currentVideoIndex}
                                src={`https://www.youtube.com/embed/${arcaVideos[currentVideoIndex].id}`}
                                title={arcaVideos[currentVideoIndex].title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            )}
                          </div>

                          {/* Video Indicators */}
                          <div className="d-flex justify-content-center gap-2 mt-3">
                            {arcaVideos.map((video, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleVideoSelect(index)}
                                className={`btn btn-sm rounded-circle ${index === currentVideoIndex ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ width: '12px', height: '12px', padding: '0', border: 'none' }}
                                aria-label={`Video ${index + 1}`}
                              ></button>
                            ))}
                          </div>
                        </div>

                        {/* Navigation Buttons Below Video */}
                        <div className="d-flex justify-content-center gap-3 mt-3">
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={handlePrevVideo}
                          >
                            <i className="fas fa-chevron-left me-2"></i>Previous
                          </button>
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={handleNextVideo}
                          >
                            Next<i className="fas fa-chevron-right ms-2"></i>
                          </button>
                        </div>
                        <p className="text-muted text-center mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                          Explore ARCA's impact and mission through our video stories (Video {currentVideoIndex + 1} of {arcaVideos.length})
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ARCA International Videos Section - Redesigned with ARCA Colors and Animations */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #f0f4f0 0%, #FFFFFF 100%)' }}>
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-5">
              <h2 className="display-5 fw-bold mb-3" style={{ color: '#6B8E23' }}>ARCA International Videos</h2>
              <p className="lead" style={{ color: '#4A4F4F' }}>Discover ARCA's mission and impact in multiple languages</p>
              <div className="mx-auto" style={{
                width: '80px',
                height: '4px',
                background: 'linear-gradient(90deg, #6B8E23 0%, #556B2F 100%)',
                borderRadius: '2px',
                marginTop: '1rem'
              }}></div>
            </div>
          </div>
          <div className="row g-4 justify-content-center">
            {[
              { title: 'Português', video: portugueseVideo, delay: 0 },
              { title: 'Français', video: frenchVideo, delay: 0.1 },
              { title: 'عربي', video: arabicVideo, delay: 0.2 }
            ].map((item, index) => (
              <div key={index} className="col-12 col-md-6 col-lg-4">
                <div
                  className="card h-100 border-0 shadow-sm"
                  style={{
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `fadeInUp 0.6s ease-out ${item.delay}s both`,
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(107, 142, 35, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {/* Header with ARCA Green */}
                  <div
                    className="text-center text-white py-3"
                    style={{
                      background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        animation: 'shimmer 2s infinite'
                      }}
                    ></div>
                    <h4 className="fw-bold mb-0" style={{ fontSize: '1.25rem', position: 'relative', zIndex: 1 }}>{item.title}</h4>
                  </div>

                  {/* Video Container */}
                  <div className="card-body p-3">
                    <div
                      className="ratio ratio-16x9"
                      style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    >
                      <video
                        controls
                        className="w-100 h-100"
                        style={{ objectFit: 'cover' }}
                      >
                        <source src={item.video} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>

      {/* Programs Section - Redesigned with ARCA Colors and Animations */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #f0f4f0 100%)' }}>
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-5">
              <h2 className="display-5 fw-bold mb-3" style={{ color: '#6B8E23' }}>What We Do</h2>
              <p className="lead" style={{ color: '#4A4F4F' }}>ARCA provides comprehensive programs to strengthen respiratory care across Africa</p>
              <div className="mx-auto" style={{
                width: '80px',
                height: '4px',
                background: 'linear-gradient(90deg, #6B8E23 0%, #556B2F 100%)',
                borderRadius: '2px',
                marginTop: '1rem'
              }}></div>
            </div>
          </div>
          <div className="row g-4">
            {programs.map((program, index) => {
              const Icon = program.icon;
              return (
                <div key={index} className="col-12 col-sm-6 col-lg-3">
                  <div className="arca-flip-card" style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both` }}>
                    <div className="arca-flip-card-inner">
                      {/* Front of card */}
                      <div className="arca-flip-card-front">
                        <div className="card-body p-4 text-center d-flex flex-column align-items-center justify-content-center h-100">
                          <div className="arca-flip-icon-circle">
                            <Icon style={{ color: '#FFFFFF' }} size={36} />
                          </div>
                          <h4 className="fw-bold mb-3" style={{ color: '#1C1C1C' }}>{program.title}</h4>
                          <p className="text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>{program.description}</p>
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                            <i className="fas fa-hand-pointer me-1"></i> Hover to learn more
                          </span>
                        </div>
                      </div>

                      {/* Back of card */}
                      <div className="arca-flip-card-back">
                        <div className="card-body p-4 text-center d-flex flex-column align-items-center justify-content-center h-100">
                          <div className="arca-flip-icon-circle-back">
                            <Icon style={{ color: '#FFFFFF' }} size={28} />
                          </div>
                          <h4 className="fw-bold mb-3" style={{ color: '#FFFFFF' }}>{program.title}</h4>
                          <p className="mb-4" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', lineHeight: '1.6' }}>{program.backDescription}</p>
                          <Link
                            to={program.link}
                            className="btn fw-semibold arca-flip-btn"
                          >
                            Learn More <i className="fas fa-arrow-right ms-2"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Flip Card CSS - Professional 3D flip animation */}
      <style>{`
        .arca-flip-card {
          perspective: 1200px;
          height: 400px;
          cursor: pointer;
        }
        .arca-flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform-style: preserve-3d;
        }
        .arca-flip-card:hover .arca-flip-card-inner {
          transform: rotateY(180deg);
        }
        .arca-flip-card-front,
        .arca-flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backfaceVisibility: 'hidden';
          -webkit-backface-visibility: hidden;
          border-radius: 12px;
          overflow: hidden;
        }
        .arca-flip-card-front {
          background-color: #FFFFFF;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
        }
        .arca-flip-card-back {
          transform: rotateY(180deg);
          background: linear-gradient(135deg, #6B8E23 0%, #556B2F 100%);
          box-shadow: 0 8px 25px rgba(107, 142, 35, 0.35);
        }
        .arca-flip-icon-circle {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #6B8E23 0%, #556B2F 100%);
          border-radius: 50%;
          box-shadow: 0 8px 20px rgba(107, 142, 35, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem auto;
        }
        .arca-flip-icon-circle-back {
          width: 60px;
          height: 60px;
          background-color: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem auto;
        }
        .arca-flip-btn {
          background-color: #FFFFFF;
          color: #6B8E23;
          border: none;
          border-radius: 25px;
          padding: 0.6rem 1.5rem;
          transition: all 0.3s ease;
        }
        .arca-flip-btn:hover {
          background-color: #f0f4f0 !important;
          color: #556B2F !important;
          transform: scale(1.05);
        }
      `}</style>

      {/* Add keyframes animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* MedTech Advisor Section - Strong Huang */}
      <section className="py-3 py-lg-4 bg-white">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm border-0">
                <div className="row g-0">
                  <div className="col-md-3">
                    <div className="h-100 overflow-hidden">
                      <img
                        src={strongHuangImg}
                        alt="Strong Huang, MedTech Advisor"
                        className="img-fluid h-100 w-100"
                        style={{ objectFit: 'cover', objectPosition: 'center', maxHeight: '280px' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-9">
                    <div className="card-body p-3">
                      <div className="mb-2">
                        <h5 className="fw-bold mb-1" style={{ color: '#6B8E23', fontSize: '1.1rem' }}>Strong Huang</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>MedTech Advisor</p>
                      </div>
                      <h3 className="h5 fw-bold text-dark mb-2">MedTech Partnerships & Innovation</h3>
                      <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                        Establishes relationships with medical device manufacturers, technology companies,
                        research institutions, and funding agencies to advance respiratory care in Africa.
                      </p>
                      <div className="bg-light rounded p-2 mb-2">
                        <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>Partner With ARCA:</h6>
                        <ul className="mb-0 ps-3" style={{ fontSize: '0.8rem' }}>
                          <li>Medical device manufacturers & technology companies</li>
                          <li>Research institutions & funding agencies</li>
                          <li>Vendors offering respiratory care solutions</li>
                        </ul>
                      </div>
                      <div className="bg-light rounded p-2 mb-2">
                        <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>Contact Information:</h6>
                        <div style={{ fontSize: '0.8rem' }}>
                          <div className="mb-1">
                            <i className="bi bi-envelope-fill me-2" style={{ color: '#6B8E23' }}></i>
                            <a href="mailto:medtech@africaresp.org" className="text-decoration-none" style={{ color: '#1e40af' }}>
                              medtech@africaresp.org
                            </a>
                          </div>
                        </div>
                      </div>
                      <div>
                        <a
                          href="mailto:medtech@africaresp.org?subject=Partnership Inquiry&body=Hello Strong,%0D%0A%0D%0AI am interested in partnering with ARCA.%0D%0A%0D%0APlease contact me at your earliest convenience.%0D%0A%0D%0AThank you."
                          className="btn btn-primary btn-sm me-2"
                        >
                          <Mail size={16} className="me-1" />
                          Email for Partnerships
                        </a>
                        <a
                          href="tel:+17633706225"
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="bi bi-telephone-fill me-1"></i>
                          Call Now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collaborators Section */}
      <section className="py-4 py-lg-5" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #6B8E23 100%)' }}>
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-4 mb-lg-5">
              <h2 className="display-6 fw-bold text-dark">Our Collaborators</h2>
              <p className="text-muted">Working together for better respiratory care across Africa</p>
            </div>
          </div>

          {/* Collaborators Grid */}
          <div className="row g-3 g-lg-4">
            {collaborators.map((collaborator, index) => (
              <div key={index} className="col-6 col-sm-4 col-md-3 col-lg-2">
                <a
                  href={collaborator.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                  style={{ display: 'block' }}
                >
                  <div className="card shadow-sm border-0 h-100 hover-shadow">
                    <div className="card-body p-3 text-center">
                      <div className="bg-light rounded d-flex align-items-center justify-content-center mb-2" style={{ height: '70px' }}>
                        {collaborator.logo ? (
                          <img src={collaborator.logo} alt={collaborator.name} style={{ maxHeight: '50px', maxWidth: '100%' }} />
                        ) : (
                          <div className="text-secondary" style={{ fontSize: '0.7rem', padding: '0.3rem' }}>
                            Logo Placeholder
                          </div>
                        )}
                      </div>
                      <p className="fw-semibold text-dark mb-0" style={{ fontSize: '0.75rem' }}>{collaborator.name}</p>
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor Partners Section */}
      <VendorsDisplay showHeader={true} maxItems={6} />

      {/* Global Presence Section - Redesigned with ARCA Colors and Animations */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #f0f4f0 100%)' }}>
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-5">
              <h2 className="display-5 fw-bold mb-3" style={{ color: '#6B8E23' }}>Our Global Presence</h2>
              <p className="lead" style={{ color: '#4A4F4F' }}>ARCA's reach spans across Africa and beyond - representing members from multiple continents</p>
              <div className="mx-auto" style={{
                width: '80px',
                height: '4px',
                background: 'linear-gradient(90deg, #6B8E23 0%, #556B2F 100%)',
                borderRadius: '2px',
                marginTop: '1rem'
              }}></div>
            </div>
          </div>

          {/* Africa Section Heading */}
          <div className="text-center mb-3">
            <h3 className="fw-bold" style={{ color: '#6B8E23', fontSize: '1.5rem' }}>Africa</h3>
          </div>

          {/* Country Flags Carousel - Enhanced with ARCA Styling */}
          <div
            className="position-relative overflow-hidden rounded-3 p-3"
            style={{
              height: '100px',
              background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)',
              boxShadow: '0 10px 30px rgba(107, 142, 35, 0.3)'
            }}
          >
            {/* Animated overlay */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                animation: 'shimmerFull 3s infinite',
                pointerEvents: 'none',
                zIndex: 1
              }}
            ></div>

            <div className="d-flex gap-3 animate-scroll-slow" style={{
              animation: 'scroll 50s linear infinite',
              whiteSpace: 'nowrap',
              position: 'relative',
              zIndex: 2
            }}>
              {[
                // African Countries (Hardcoded)
                { code: 'dz', name: 'Algeria', type: 'african' },
                { code: 'ao', name: 'Angola', type: 'african' },
                { code: 'bj', name: 'Benin', type: 'african' },
                { code: 'bw', name: 'Botswana', type: 'african' },
                { code: 'bf', name: 'Burkina Faso', type: 'african' },
                { code: 'bi', name: 'Burundi', type: 'african' },
                { code: 'cm', name: 'Cameroon', type: 'african' },
                { code: 'cv', name: 'Cape Verde', type: 'african' },
                { code: 'cf', name: 'Central African Republic', type: 'african' },
                { code: 'td', name: 'Chad', type: 'african' },
                { code: 'km', name: 'Comoros', type: 'african' },
                { code: 'cg', name: 'Congo', type: 'african' },
                { code: 'cd', name: 'DR Congo', type: 'african' },
                { code: 'dj', name: 'Djibouti', type: 'african' },
                { code: 'eg', name: 'Egypt', type: 'african' },
                { code: 'gq', name: 'Equatorial Guinea', type: 'african' },
                { code: 'er', name: 'Eritrea', type: 'african' },
                { code: 'et', name: 'Ethiopia', type: 'african' },
                { code: 'ga', name: 'Gabon', type: 'african' },
                { code: 'gm', name: 'Gambia', type: 'african' },
                { code: 'gh', name: 'Ghana', type: 'african' },
                { code: 'gn', name: 'Guinea', type: 'african' },
                { code: 'gw', name: 'Guinea-Bissau', type: 'african' },
                { code: 'ci', name: 'Ivory Coast', type: 'african' },
                { code: 'ke', name: 'Kenya', type: 'african' },
                { code: 'ls', name: 'Lesotho', type: 'african' },
                { code: 'lr', name: 'Liberia', type: 'african' },
                { code: 'ly', name: 'Libya', type: 'african' },
                { code: 'mg', name: 'Madagascar', type: 'african' },
                { code: 'mw', name: 'Malawi', type: 'african' },
                { code: 'ml', name: 'Mali', type: 'african' },
                { code: 'mr', name: 'Mauritania', type: 'african' },
                { code: 'mu', name: 'Mauritius', type: 'african' },
                { code: 'ma', name: 'Morocco', type: 'african' },
                { code: 'mz', name: 'Mozambique', type: 'african' },
                { code: 'na', name: 'Namibia', type: 'african' },
                { code: 'ne', name: 'Niger', type: 'african' },
                { code: 'ng', name: 'Nigeria', type: 'african' },
                { code: 'rw', name: 'Rwanda', type: 'african' },
                { code: 'st', name: 'São Tomé and Príncipe', type: 'african' },
                { code: 'sn', name: 'Senegal', type: 'african' },
                { code: 'sc', name: 'Seychelles', type: 'african' },
                { code: 'sl', name: 'Sierra Leone', type: 'african' },
                { code: 'so', name: 'Somalia', type: 'african' },
                { code: 'za', name: 'South Africa', type: 'african' },
                { code: 'ss', name: 'South Sudan', type: 'african' },
                { code: 'sd', name: 'Sudan', type: 'african' },
                { code: 'sz', name: 'Eswatini', type: 'african' },
                { code: 'tz', name: 'Tanzania', type: 'african' },
                { code: 'tg', name: 'Togo', type: 'african' },
                { code: 'tn', name: 'Tunisia', type: 'african' },
                { code: 'ug', name: 'Uganda', type: 'african' },
                { code: 'zm', name: 'Zambia', type: 'african' },
                { code: 'zw', name: 'Zimbabwe', type: 'african' }
              ].concat([
                // Duplicate for seamless loop - African Countries
                { code: 'dz', name: 'Algeria', type: 'african' },
                { code: 'ao', name: 'Angola', type: 'african' },
                { code: 'bj', name: 'Benin', type: 'african' },
                { code: 'bw', name: 'Botswana', type: 'african' },
                { code: 'bf', name: 'Burkina Faso', type: 'african' },
                { code: 'bi', name: 'Burundi', type: 'african' },
                { code: 'cm', name: 'Cameroon', type: 'african' },
                { code: 'cv', name: 'Cape Verde', type: 'african' },
                { code: 'cf', name: 'Central African Republic', type: 'african' },
                { code: 'td', name: 'Chad', type: 'african' },
                { code: 'km', name: 'Comoros', type: 'african' },
                { code: 'cg', name: 'Congo', type: 'african' },
                { code: 'cd', name: 'DR Congo', type: 'african' },
                { code: 'dj', name: 'Djibouti', type: 'african' },
                { code: 'eg', name: 'Egypt', type: 'african' },
                { code: 'gq', name: 'Equatorial Guinea', type: 'african' },
                { code: 'er', name: 'Eritrea', type: 'african' },
                { code: 'et', name: 'Ethiopia', type: 'african' },
                { code: 'ga', name: 'Gabon', type: 'african' },
                { code: 'gm', name: 'Gambia', type: 'african' },
                { code: 'gh', name: 'Ghana', type: 'african' },
                { code: 'gn', name: 'Guinea', type: 'african' },
                { code: 'gw', name: 'Guinea-Bissau', type: 'african' },
                { code: 'ci', name: 'Ivory Coast', type: 'african' },
                { code: 'ke', name: 'Kenya', type: 'african' },
                { code: 'ls', name: 'Lesotho', type: 'african' },
                { code: 'lr', name: 'Liberia', type: 'african' },
                { code: 'ly', name: 'Libya', type: 'african' },
                { code: 'mg', name: 'Madagascar', type: 'african' },
                { code: 'mw', name: 'Malawi', type: 'african' },
                { code: 'ml', name: 'Mali', type: 'african' },
                { code: 'mr', name: 'Mauritania', type: 'african' },
                { code: 'mu', name: 'Mauritius', type: 'african' },
                { code: 'ma', name: 'Morocco', type: 'african' },
                { code: 'mz', name: 'Mozambique', type: 'african' },
                { code: 'na', name: 'Namibia', type: 'african' },
                { code: 'ne', name: 'Niger', type: 'african' },
                { code: 'ng', name: 'Nigeria', type: 'african' },
                { code: 'rw', name: 'Rwanda', type: 'african' },
                { code: 'st', name: 'São Tomé and Príncipe', type: 'african' },
                { code: 'sn', name: 'Senegal', type: 'african' },
                { code: 'sc', name: 'Seychelles', type: 'african' },
                { code: 'sl', name: 'Sierra Leone', type: 'african' },
                { code: 'so', name: 'Somalia', type: 'african' },
                { code: 'za', name: 'South Africa', type: 'african' },
                { code: 'ss', name: 'South Sudan', type: 'african' },
                { code: 'sd', name: 'Sudan', type: 'african' },
                { code: 'sz', name: 'Eswatini', type: 'african' },
                { code: 'tz', name: 'Tanzania', type: 'african' },
                { code: 'tg', name: 'Togo', type: 'african' },
                { code: 'tn', name: 'Tunisia', type: 'african' },
                { code: 'ug', name: 'Uganda', type: 'african' },
                { code: 'zm', name: 'Zambia', type: 'african' },
                { code: 'zw', name: 'Zimbabwe', type: 'african' }
              ]).map((country, index) => (
                <div
                  key={index}
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{
                    minWidth: '75px',
                    display: 'inline-flex',
                    padding: '4px',
                    animation: `fadeInScale 0.6s ease-out ${(index % 10) * 0.05}s both`
                  }}
                >
                  <div
                    className={`text-center rounded shadow-sm p-2 d-flex flex-column align-items-center ${country.type === 'international' ? 'border-3' : ''}`}
                    style={{
                      width: '70px',
                      height: '75px',
                      background: country.type === 'international'
                        ? 'linear-gradient(135deg, #FFFFFF 0%, #FFE5B4 100%)'
                        : 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
                      border: country.type === 'international' ? '2px solid #FFD700' : '1px solid rgba(255,255,255,0.3)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px) scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 10px 20px rgba(255,255,255,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    title={country.type === 'international' ? `${country.name} (International Member)` : country.name}
                  >
                    {country.type === 'international' && (
                      <div
                        className="badge bg-warning text-dark position-absolute"
                        style={{
                          top: '-5px',
                          right: '-5px',
                          fontSize: '0.4rem',
                          padding: '0.1rem 0.2rem',
                          animation: 'pulse 2s infinite',
                          zIndex: 10
                        }}
                      >
                        ⭐
                      </div>
                    )}
                    <span className={`fi fi-${country.code}`} style={{ width: '32px', height: '24px', fontSize: '24px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}></span>
                    <p
                      className="fw-semibold mb-0 text-center"
                      style={{
                        fontSize: '0.5rem',
                        lineHeight: '1',
                        marginTop: '3px',
                        color: '#2C3E50',
                        maxWidth: '65px',
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {country.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <style>{`
            @keyframes scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes shimmerFull {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            @keyframes fadeInScale {
              from {
                opacity: 0;
                transform: scale(0.8);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.2); opacity: 0.8; }
            }
            .animate-scroll:hover {
              animation-play-state: paused;
            }
            .animate-scroll-slow:hover {
              animation-play-state: paused;
            }
            .hover-shadow:hover {
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              transform: translateY(-2px);
              transition: all 0.2s ease-in-out;
            }
          `}</style>
        </div>
      </section>

      {/* International Members Section - Same Design as African Flags */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #f0f4f0 100%)' }}>
        <div className="container">
          {/* International Section Heading */}
          <div className="text-center mb-3">
            <h3 className="fw-bold" style={{ color: '#6B8E23', fontSize: '1.5rem' }}>International</h3>
          </div>

          {/* Country Flags Carousel - Enhanced with ARCA Styling */}
          <div
            className="position-relative overflow-hidden rounded-3 p-3"
            style={{
              height: '100px',
              background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)',
              boxShadow: '0 10px 30px rgba(107, 142, 35, 0.3)'
            }}
          >
            {/* Animated overlay */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                animation: 'shimmerFull 3s infinite',
                pointerEvents: 'none',
                zIndex: 1
              }}
            ></div>

            <div className="d-flex gap-3 animate-scroll-slow" style={{
              animation: 'scroll 50s linear infinite',
              whiteSpace: 'nowrap',
              position: 'relative',
              zIndex: 2
            }}>
              {loadingFlags ? (
                // Show loading placeholder
                <div className="text-white text-center w-100 py-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading international flags...
                </div>
              ) : (
                internationalFlags.concat(internationalFlags).map((country, index) => (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      minWidth: '75px',
                      display: 'inline-flex',
                      padding: '4px',
                      animation: `fadeInScale 0.6s ease-out ${(index % 10) * 0.05}s both`
                    }}
                  >
                    <div
                      className="text-center rounded shadow-sm p-2 d-flex flex-column align-items-center"
                      style={{
                        width: '70px',
                        height: '75px',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-6px) scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 10px 20px rgba(255,255,255,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                      title={country.name}
                    >
                      <span className={`fi fi-${country.code}`} style={{ width: '32px', height: '24px', fontSize: '24px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}></span>
                      <p
                        className="fw-semibold mb-0 text-center"
                        style={{
                          fontSize: '0.5rem',
                          lineHeight: '1',
                          marginTop: '3px',
                          color: '#2C3E50',
                          maxWidth: '65px',
                          wordWrap: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {country.name}
                      </p>
                    </div>
                  </div>
                )))
              }
            </div>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
};

export default HomePage;